import { Container, Section, Eyebrow } from '@/components/primitives/Container';
import { Badge } from '@/components/primitives/Badge';
import { MCP_TOOLS, MCP_SERVER_INFO } from '@/data/mcp-tools';

export function Mcp() {
  return (
    <>
      <Section className="pt-12 sm:pt-16">
        <Container width="editorial">
          <Eyebrow>Model Context Protocol</Eyebrow>
          <h1 className="mt-4 font-serif text-4xl font-normal tracking-tight sm:text-5xl">
            Inteligencia curricular argentina, disponible vía MCP.
          </h1>
          <p className="mt-6 max-w-prose text-lg leading-relaxed text-ink-muted">
            Tu agente de IA puede consultar el corpus curricular argentino con la misma fidelidad que
            SICA. Tres herramientas tipadas, dos modos de transporte, política de citas heredada.
          </p>
        </Container>
      </Section>

      <Section className="pt-0">
        <Container>
          <div className="grid gap-10 lg:grid-cols-[14rem_1fr]">
            <aside className="hidden lg:block">
              <nav aria-label="Secciones" className="sticky top-24">
                <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">
                  En esta página
                </p>
                <ul className="mt-4 flex flex-col gap-2 text-sm">
                  <li>
                    <a href="#quickstart" className="focus-ring rounded text-ink-muted hover:text-ink">
                      Quickstart
                    </a>
                  </li>
                  <li>
                    <a href="#tools" className="focus-ring rounded text-ink-muted hover:text-ink">
                      Tools expuestas
                    </a>
                  </li>
                  <li>
                    <a href="#transport" className="focus-ring rounded text-ink-muted hover:text-ink">
                      Transports
                    </a>
                  </li>
                  <li>
                    <a href="#fuentes" className="focus-ring rounded text-ink-muted hover:text-ink">
                      Política de fuentes
                    </a>
                  </li>
                </ul>
              </nav>
            </aside>

            <div className="max-w-editorial">
              <section id="quickstart" className="scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight">Quickstart</h2>
                <p className="mt-3 text-base leading-relaxed text-ink-muted">
                  Conectá tu agente al servidor MCP de SICA. Soporta stdio (default, para integración
                  CLI local) y HTTP/SSE (para OpenAI Platform y servidores deployados).
                </p>

                <h3 className="mt-8 text-sm font-semibold uppercase tracking-wider text-ink">
                  1. Stdio (Claude Desktop, Cursor, Codex CLI)
                </h3>
                <CodeBlock language="bash">
{`MCP_TRANSPORT=stdio node dist/index.js`}
                </CodeBlock>

                <h3 className="mt-6 text-sm font-semibold uppercase tracking-wider text-ink">
                  2. HTTP (OpenAI Platform)
                </h3>
                <CodeBlock language="bash">
{`MCP_TRANSPORT=http MCP_PORT=${MCP_SERVER_INFO.defaultPort} node dist/index.js
# Endpoint: http://localhost:${MCP_SERVER_INFO.defaultPort}/mcp
# Health:   http://localhost:${MCP_SERVER_INFO.defaultPort}/health`}
                </CodeBlock>

                <h3 className="mt-6 text-sm font-semibold uppercase tracking-wider text-ink">
                  3. Configuración OpenAI Platform
                </h3>
                <CodeBlock language="json">
{`{
  "type": "mcp",
  "server_label": "sica",
  "server_url": "http://your-server.com:${MCP_SERVER_INFO.defaultPort}/mcp",
  "require_approval": "never"
}`}
                </CodeBlock>
              </section>

              <section id="tools" className="mt-16 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight">Tools expuestas</h2>
                <p className="mt-3 text-base leading-relaxed text-ink-muted">
                  Tres herramientas tipadas. Las definiciones reflejan exactamente las del servidor
                  ({MCP_SERVER_INFO.name} v{MCP_SERVER_INFO.version}).
                </p>

                <div className="mt-8 flex flex-col gap-10">
                  {MCP_TOOLS.map((tool) => (
                    <article key={tool.name} id={`tool-${tool.name}`} className="scroll-mt-24">
                      <header className="flex flex-wrap items-center gap-3">
                        <h3 className="font-mono text-base text-ink">{tool.name}</h3>
                        <Badge tone="accent">tool</Badge>
                      </header>
                      <p className="mt-2 text-base leading-relaxed text-ink-muted">
                        {tool.description}
                      </p>

                      <h4 className="mt-5 text-xs font-semibold uppercase tracking-wider text-ink-muted">
                        Parámetros
                      </h4>
                      <dl className="mt-3 divide-y divide-line border-y border-line">
                        {tool.params.map((p) => (
                          <div key={p.name} className="grid grid-cols-[8rem_1fr] gap-4 py-3">
                            <dt className="font-mono text-sm">
                              <span className="text-ink">{p.name}</span>
                              {p.required && (
                                <span className="ml-2 text-[10px] uppercase tracking-wider text-status-progress">
                                  required
                                </span>
                              )}
                            </dt>
                            <dd className="text-sm text-ink-muted">
                              <span className="font-mono text-xs text-ink-subtle">{p.type}</span>
                              <span className="ml-2">{p.description}</span>
                            </dd>
                          </div>
                        ))}
                      </dl>

                      <h4 className="mt-5 text-xs font-semibold uppercase tracking-wider text-ink-muted">
                        Ejemplo
                      </h4>
                      <CodeBlock language="json">{tool.example}</CodeBlock>
                    </article>
                  ))}
                </div>
              </section>

              <section id="transport" className="mt-16 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight">Transports</h2>
                <p className="mt-3 text-base leading-relaxed text-ink-muted">
                  El servidor MCP de SICA soporta {MCP_SERVER_INFO.transports.join(', ')}. Stdio es
                  el default para integraciones locales (Claude Desktop, Cursor, Codex CLI). HTTP y
                  SSE son para servidores deployados o integración con OpenAI Platform.
                </p>
                <p className="mt-4 text-base leading-relaxed text-ink-muted">
                  Versión del protocolo MCP soportada: <code className="font-mono text-sm text-ink">{MCP_SERVER_INFO.protocolVersion}</code>.
                </p>
              </section>

              <section id="fuentes" className="mt-16 scroll-mt-24">
                <h2 className="font-serif text-2xl font-normal tracking-tight">
                  Política de fuentes para integradores
                </h2>
                <p className="mt-3 text-base leading-relaxed text-ink-muted">
                  La promesa de SICA es trazabilidad. Cuando tu agente recibe una respuesta de SICA
                  con cita, debe exponerla al usuario final. No es un nice-to-have: es lo que justifica
                  que el corpus curricular argentino se exponga vía MCP.
                </p>
                <p className="mt-4 text-base leading-relaxed text-ink-muted">
                  Concretamente: si <code className="font-mono text-sm text-ink">search_standards</code> devuelve un
                  fragmento con documento y página, el agente integrador debe mostrar esa metadata al
                  usuario, no resumirla como prosa propia.
                </p>
              </section>
            </div>
          </div>
        </Container>
      </Section>
    </>
  );
}

function CodeBlock({ language, children }: { language: string; children: string }) {
  return (
    <pre className="mt-3 overflow-x-auto rounded-md border border-line bg-ink p-4 text-xs leading-relaxed text-paper">
      <code className={`language-${language} font-mono`}>{children}</code>
    </pre>
  );
}
